import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import AdvisorWizard from "@/components/AdvisorWizard";
import RentingOptions from "@/components/RentingOptions";
import ContactSection from "@/components/ContactSection";
import HeyflowBanner from "@/components/HeyflowBanner";
import ComparisonTable from "@/components/ComparisonTable";
import ProvenExpertBadge from "@/components/ProvenExpertBadge";

const Index = () => {
  return (
    <>
      <Helmet>
        <title>Eigenheimverrentung: Wohnrecht, Leibrente & Teilverkauf</title>
        <meta 
          name="description" 
          content="Haus verrenten & wohnen bleiben: Wohnrecht, Leibrente, Nießbrauch, Teilverkauf, Rückmiete. Kostenlose Beratung zur Seniorenfinanzierung."
        />
        <meta name="keywords" content="wohnrecht, leibrente, seniorenfinanzierung, niessbrauchrecht, teilverkauf, verkaufen aber wohnen bleiben, verkaufenundbleiben, rückmiete, immobilie verrenten, auszahlung haus rentner, kapital aus der immobilie, haus verrenten, wohnung verrenten, haus verkaufen und wohnen bleiben, haus verkaufen makler, haus als rentner verkaufen, eigenheimverrentung" />
        <link rel="canonical" href="https://eigenheimverrentung.de/" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Eigenheimverrentung: Haus verkaufen und wohnen bleiben" />
        <meta property="og:description" content="Professionelle Beratung zu Wohnrecht, Leibrente, Teilverkauf & mehr. Bleiben Sie wohnen und erhalten finanzielle Sicherheit im Alter." />
        <meta property="og:url" content="https://eigenheimverrentung.de/" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Eigenheimverrentung: Haus verkaufen und wohnen bleiben" />
        <meta name="twitter:description" content="Professionelle Beratung zu Wohnrecht, Leibrente, Teilverkauf & mehr. Bleiben Sie wohnen und erhalten finanzielle Sicherheit im Alter." />
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://eigenheimverrentung.de/#organization",
            "name": "RentePlusImmobilie",
            "alternateName": "eigenheimverrentung.de",
            "url": "https://eigenheimverrentung.de",
            "logo": "https://eigenheimverrentung.de/logo.png",
            "telephone": "+49-89-628246446",
            "email": "info@renteplusimmobilie.de",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "DE"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Deutschland"
            },
            "slogan": "Ihr Weg zu mehr finanzieller Sicherheit im Alter",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+49-89-628246446",
              "contactType": "customer service",
              "email": "info@renteplusimmobilie.de",
              "availableLanguage": "German"
            }
          })}
        </script>

        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Was ist Eigenheimverrentung?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Eigenheimverrentung ermöglicht es Senioren, Kapital aus ihrer Immobilie zu lösen und trotzdem wohnen zu bleiben. Verschiedene Modelle wie Leibrente, Wohnrecht, Nießbrauch, Teilverkauf und Rückmiete bieten unterschiedliche Vorteile."
                }
              },
              {
                "@type": "Question", 
                "name": "Welche Verrentungsmodelle gibt es?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Es gibt verschiedene Modelle: Leibrente (monatliche Rente), Wohnrecht (Einmalzahlung), Nießbrauch (Übertragung an Erben), Teilverkauf (nur Teil verkaufen), Rückmiete (komplett verkaufen, anmieten) und Seniorenfinanzierung (Kredit ohne Raten)."
                }
              },
              {
                "@type": "Question",
                "name": "Ist die Beratung wirklich kostenlos?", 
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, die Erstberatung ist komplett kostenlos und unverbindlich. Wir beraten neutral zu allen Verrentungsmodellen."
                }
              }
            ]
          })}
        </script>

        {/* Local Business Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://eigenheimverrentung.de/#localbusiness",
            "name": "RentePlusImmobilie - Eigenheimverrentung",
            "url": "https://eigenheimverrentung.de",
            "telephone": "+49-89-628246446",
            "email": "info@renteplusimmobilie.de",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "DE"
            },
            "description": "Kostenlose Beratung zur Eigenheimverrentung – Haus verkaufen und wohnen bleiben.",
            "priceRange": "€€",
            "openingHours": "Mo-Fr 09:00-18:00"
          })}
        </script>

        {/* BreadcrumbList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Startseite",
                "item": "https://eigenheimverrentung.de/"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        <main>
          <Hero />
          <div id="advisor-wizard">
            <AdvisorWizard />
          </div>
          <RentingOptions />
          <ComparisonTable />
          <ProvenExpertBadge />
          <ContactSection />
          <HeyflowBanner />
        </main>
        <footer className="py-4 flex justify-center">
          <Link 
            to="/admin/login" 
            className="w-2 h-2 rounded-full bg-muted-foreground/20 hover:bg-muted-foreground/40 transition-colors"
            aria-label="Admin Login"
          />
        </footer>
      </div>
    </>
  );
};

export default Index;
